import { GasType, PsychrometerProperties } from './CalculatorConditions';
/*
 * Copyright (c) Vaisala Oyj. All rights reserved.
 */

// Constants
const MH2O = 18.015; //18.0146; // Molar mass of water g/mol
const MAIR = 28.96443; // Molar mass of air g/mol

const Cpw = 1.84; // Specific heat capacity of water vapor at constant pressure at 0 °C temperature kJ/kg°C
const hwe = 2501; // Evaporation heat of water in 0 °C kJ/kg
const R = 8.3145; // J*K^-1*mol^-1

export const eis = (t: number) => {
  // t=temperature in °C. Saturation vapor pressure in hPa without enhancement factor
  const tKelv = t + 273.15;
  const A = [1, -6.0245282e3, 2.932707e1, 1.0613868e-2, -1.3198825e-5, -4.9382577e-1, 1];

  const eis = (A[0] * Math.exp(A[1] / tKelv + A[2] + A[3] * tKelv + A[4] * tKelv ** 2 + A[5] * Math.log(A[6] * tKelv))) / 100;

  return eis;
};

export const ews = (t: number) => {
  // t=temperature in °C. Saturation vapor pressure in hPa without enhancement factor
  const tKelv = t + 273.15;
  const A = [1, -6.0969385e3, 2.12409642e1, -2.711193e-2, 1.673952e-5, 2.433502, 1];

  const ews = (A[0] * Math.exp(A[1] / tKelv + A[2] + A[3] * tKelv + A[4] * tKelv ** 2 + A[5] * Math.log(A[6] * tKelv))) / 100;

  return ews;
};

export const enhancementFactor = (t: number, p: number, gasType: GasType) => {
  // p= Total pressure in HPa, t = temperature in °C.
  // For ambient pressure and lower pressures this effect is typically
  // ignored. But at pressures significantly above ambient it has to be taken
  // into account.For CO2-free air the following equation for f has been
  // proposed (Greenspan: J. of Research of the NBS vol 80A, No. 1 p 41-44)

  // Enhancement factor applies only if Air or N2 is chosen
  if (!['gas.Air', 'gas.N2'].includes(gasType)) return 1;

  let A = [];
  let B = [];
  let saturationPressure;

  // The parameters Ai and Bi: ice
  if (t >= -100 && t < 0) {
    saturationPressure = eis(t);
    A = [3.64449e-4, 2.9367585e-5, 4.8874766e-7, 4.3669918e-9];
    B = [-10.7271, 7.6215115e-2, -1.7490155e-4, 2.4668279e-6];
  }
  // The parameters Ai and Bi: water
  else if (t >= 0 && t <= 100) {
    saturationPressure = ews(t);
    A = [3.53624e-4, 2.9328363e-5, 2.6168979e-7, 8.5813609e-9];
    B = [-1.07588e1, 6.3268134e-2, -2.5368934e-4, 6.3405286e-7];
  } else {
    return 1;
  }
  // Out of specs if pressure is higher than 2 MPa == 20000 hPa
  if (p > 20000) {
    // console.warn('Pressure out of specs');
    return 1;
  }

  const alpha = A[0] + A[1] * t + A[2] * t ** 2 + A[3] * t ** 3;
  const beta = Math.exp(B[0] + B[1] * t + B[2] * t ** 2 + B[3] * t ** 3);

  let f = Math.exp(alpha * (1 - saturationPressure / p) + beta * (p / saturationPressure - 1));

  if (f < 1) {
    f = 1;
  }
  return f;
};

// t=temperature in °C, p=pressure in hPa
// returns water saturation pressure in hPa. Compensated with enchancement factor
export const pws = (t: number, p: number, gasType: GasType) => {
  // console.log(ews(t), enhancementFactor(t, p, gasType));
  return ews(t) * enhancementFactor(t, p, gasType);
};

// t=temperature in °C, p=pressure in hPa
// returns water saturation pressure (over ice) in hPa. Compensated with enchancement factor
export const pis = (t: number, p: number, gasType: GasType) => eis(t) * enhancementFactor(t, p, gasType);

// Calculation functions from water vapor pressure to other humidity parameters
// ****************************************************************************

// Relative humidity is at all temperatures and pressures defined as the ratio of the water vapour pressure to the saturation water vapour pressure (over water) at the gas temperature
export const pwToRh = (pw: number, pws: number) => (pw / pws) * 100;

// Relative humidity (Frost) is at temperatures below 0 and all pressures defined as the ratio of the water vapour pressure to the saturation water vapour pressure (over ice) at the gas temperature
export const pwToRhi = (pw: number, pis: number) => (pw / pis) * 100;

export const pwToTd = (pw: number, t: number, p: number, gasType: GasType) => {
  const SaturationPressure = pws(t, p, gasType);
  const rh = pwToRh(pw, SaturationPressure);

  let tdMax = t + 5;
  let tdMin = -100;

  while (tdMax - tdMin > 0.000001) {
    const tdAverage = (tdMin + tdMax) / 2;
    const pwAverage = pws(tdAverage, p, gasType);
    let rhAverage = pwToRh(pwAverage, SaturationPressure);

    if (rhAverage >= rh) {
      tdMax = tdAverage;
    } else {
      tdMin = tdAverage;
    }
  }

  return (tdMin + tdMax) / 2;
};

export const pwToTdf = (pw: number, t: number, p: number, gasType: GasType) => {
  // Use this function to calculate Tdf only if pwToTd returns a negative value (< 0 DegC)

  const SaturationPressure = pis(t, p, gasType);
  const rhi = pwToRhi(pw, SaturationPressure);

  let tdfMax = 0;
  let tdfMin = -100;

  while (tdfMax - tdfMin > 0.000001) {
    const tdfAverage = (tdfMin + tdfMax) / 2;
    const pwAverage = pis(tdfAverage, p, gasType);
    let rhiAverage = pwToRhi(pwAverage, SaturationPressure);

    if (rhiAverage >= rhi) {
      tdfMax = tdfAverage;
    } else {
      tdfMin = tdfAverage;
    }
  }

  return (tdfMin + tdfMax) / 2;
};

export const pwToTw = (pw: number, t: number, p: number, K: PsychrometerProperties, gasType: GasType) => {
  const SaturationPressure = pws(t, p, gasType);
  const rh = pwToRh(pw, SaturationPressure);

  let twMax = t + 5;
  let twMin = -100;

  while (twMax - twMin > 0.000001) {
    const twAverage = (twMin + twMax) / 2;
    const pwAverage = twToPw(twAverage, t, p, K, gasType);
    let rhAverage = pwToRh(pwAverage, SaturationPressure);

    if (rhAverage >= rh) {
      twMax = twAverage;
    } else {
      twMin = twAverage;
    }
  }

  return (twMin + twMax) / 2;
};

// export const pwToTwf = (pw: number, t: number, p: number, K: number) => {
//   // Use this function to calculate Twf only if pwToTw returns a negative value (< 0 DegC)
//   // TODO: Implement psychrometric constant for ice

//   const SaturationPressure = pis(t, p);
//   const rhi = pwToRhi(pw, SaturationPressure);

//   let twfMax = 0;
//   let twfMin = -100;

//   while (twfMax - twfMin > 0.000001) {
//     const twfAverage = (twfMin + twfMax) / 2;
//     const pwAverage = twfToPw(twfAverage, t, p, K);
//     let rhiAverage = pwToRhi(pwAverage, SaturationPressure);

//     if (rhiAverage >= rhi) {
//       twfMax = twfAverage;
//     } else {
//       twfMin = twfAverage;
//     }
//   }
//   console.warn("psychrometric constant for ice is not implemented")
//   return (twfMin + twfMax) / 2;
// };

// Calculate Absolute humidity from vapor pressure
export const pwToA = (pw: number, t: number) => {
  return (MH2O * pw * 100) / (R * (t + 273.15));
};
// Calculate Mixing ratio from vapor pressure
export const pwToX = (pw: number, p: number, Mgas: number) => {
  const X = 1000 * (MH2O / Mgas) * (pw / (p - pw));
  if (X > 1000000 || X < 0) return NaN;
  else return X;
};
// Calculate enthalpy
export const pwToH = (pw: number, t: number, tRef: number, p: number, Mgas: number, Cpg: number) =>
  Cpg * (t - tRef) + (pwToX(pw, p, Mgas) / 1000) * (Cpw * (t - tRef) + hwe);

// Parts Per Million ppmv(dry) volume/volume
// In dry conditions, parts per million values are usually given vs. the amount of dry air'''
export const pwToPpmv = (pw: number, p: number) => {
  const PPMv = (pw / (p - pw)) * 1e6;
  if (PPMv > 1000000 || PPMv < 0) return NaN;
  else return PPMv;
};

// Parts Per Million ppmv(wet) volume/volume
export const pwToPpmvwet = (pw: number, p: number) => (pw / p) * 1e6;

// Calculation functions from other humidity parameters into water vapor pressure
// ******************************************************************************

// Calculate vapor pressure from RH and saturation vapor pressure
export const rhToPw = (rh: number, pws: number) => (rh / 100) * pws;
// Calculate vapor pressure from RHi and saturation vapor pressure
export const rhiToPw = (rhi: number, pis: number) => (rhi / 100) * pis;
// Calculate vapor pressure from Dew point temperature
export const tdToPw = (td: number, p: number, gasType: GasType) => pws(td, p, gasType);
// Calculate vapor pressure from Frost point temperature
export const tdfToPw = (tdf: number, p: number, gasType: GasType) => {
  if (tdf >= 0) {
    return pws(tdf, p, gasType);
  } else {
    return pis(tdf, p, gasType);
  }
};
// Calculate vapor pressure from wet bulb temperature
export const twToPw = (tw: number, t: number, p: number, K: PsychrometerProperties, gasType: GasType) => {
  const result = pws(tw, p, gasType) - p * K.psychrometerConstantA * (t - tw) * (1 + K.psychrometerConstantB * tw);
  return result > 0 ? result : 0;
};
// Calculate vapor pressure from Frost bulb temperature. Psychrometric constant for ice not implemented
// export const twfToPw = (twf: number, t: number, p: number, K: number) => {
//   if (twf >= 0) {
//     return twToPw(twf, t, p, K);
//   } else {
//     return pis(twf, p) - p * K * (t - twf);
//   }
// };

// Calculate vapor pressure from Absolute humidity
export const aToPw = (a: number, t: number) => {
  return (a * (R * (t + 273.15))) / (MH2O * 100);
};

export const xToPw = (x: number, p: number, Mgas: number) => (x * p) / ((MH2O / Mgas) * 1000 + x);

// Enthalpy calculation. tRef is reference temperature. In SI-units 0 °C. In imperial units 0 °F = -17.778 °C
export const hToPw = (h: number, t: number, tRef: number, p: number, Mgas: number, Cpg: number) => {
  const x = (1000 * (h - Cpg * (t - tRef))) / (Cpw * (t - tRef) + hwe); // g/kg

  return (x * p) / ((MH2O / Mgas) * 1000 + x);
};

export const ppmvToPw = (ppmv: number, p: number) => (ppmv * p) / (1e6 + ppmv);

export const ppmvwetToPw = (ppmvwet: number, p: number) => (ppmvwet * p) / 1e6;

const gasDensity = (t: number, p: number, x: number) => {
  // Works ok, but room for improvement:
  // https://www.engineeringtoolbox.com/density-air-d_680.html
  const xKgKg = x / 1000;
  const tKelv = t + 273.15;
  return 1 / ((((R * tKelv) / (p * MH2O * 0.1)) * (MH2O / MAIR + xKgKg)) / (1 + xKgKg));
};

// **************************
// H2O2 specific calculations
// **************************

// H2O2 related constants
const MH2O2 = 34.014; //34.0136; // Molar mass of Hydrogen Peroxide g/mol
const RLATM = 0.08206; // Gas constant: liter atm / mol K

// Water density
const A = [-3.983035, 301.797, 522528.9, 69.34881, 0.99997495];
const WaterDensity = (t: number) => A[4] * (1 - ((t + A[0]) ** 2 * (t + A[1])) / (A[2] * (t + A[3])));

// H2O2-water mixture density constants
const Jb = 0.39763;
const Jc = 0.02206;
const Jd = 0.05187;
const Kb = -2.8732 / 1000;
const Kc = 3.5357 / 1000;
const Kd = -1.9414 / 1000;
const Lb = 3.2488 / 100000;
const Lc = -6.0947 / 100000;
const Ld = 3.9061 / 100000;
const Mb = -1.6363 / 10000000;
const Mc = 3.6165 / 10000000;
const Md = -2.55 / 10000000;

const wActivity = (xw: number, t: number) => {
  const tKelv = t + 273.15;
  return Math.exp(((1 - xw) ** 2 / (1.985877534 * tKelv)) * (-1017 + 0.97 * tKelv + 85 * (1 - 4 * xw) + 13 * (1 - 2 * xw) * (1 - 6 * xw)));
};

const hActivity = (xw: number, t: number) => {
  const tKelv = t + 273.15;
  return Math.exp((xw ** 2 / (1.985877534 * tKelv)) * (-1017 + 0.97 * tKelv + 85 * (3 - 4 * xw) + 13 * (1 - 2 * xw) * (5 - 6 * xw)));
};

const ehs = (t: number) => {
  // Calculates 100%-w H2O2 liquid saturation vapor pressure [hPa]
  // W.C. Scumb et al., "Hydrogen Peroxide," Amer. Chem. Soc. Monograph (1955).
  const tKelv = t + 273.15;
  return 10 ** (44.576 + -4025.37 / tKelv + -12.996 * (Math.log(tKelv) / Math.log(10)) + 0.0046055 * tKelv) / 0.0075006 / 100;
};

const flowToWetFlow = (flow: number, p: number, pw: number) => {
  //Convert actual volumetric flow into substance flow mol/min

  return (pw / p) * (flow / (RLATM * 273.15));
};

const liquidMassConcToDensity = (massPercent: number, t: number) => {
  // Calculates H2O2 liquid density [g/ml] from mass percent [%]
  // Water density: M. Tanaka et al., "Recommended table dor the density of water between 0C and 40C based on
  // recent experimental reports" Metrologia 38, 301-309 (2011).
  // H2O2-water mixture density: W.C. Scumb et al., "Hydrogen Peroxide," Amer. Chem. Soc. Monograph (1955).
  // Input parameters:
  //   massPercent = H2O2 mass percent [w-%]
  //   T = Temperature of liquid ['C]
  // Output parameter:
  //   H2O2_massPercent_to_d = H2O2 liquid density [um/cm3]

  // Water density
  const wDensity = WaterDensity(t);

  // H2O2-water mixture density
  const B = Jb + Kb * t + Lb * t ** 2 + Mb * t ** 3;
  const c = Jc + Kc * t + Lc * t ** 2 + Mc * t ** 3;
  const d = Jd + Kd * t + Ld * t ** 2 + Md * t ** 3;

  const P = wDensity + B * massPercent + c * massPercent ** 2 + d * massPercent ** 3;

  return P;
};

const densityToLiquidMassConc = (density: number, t: number) => {
  // Calculates H2O2 mass percent [%] from liquid density [g/ml]
  // Water density: M. Tanaka et al., "Recommended table dor the density of water between 0C and 40C based on
  // recent experimental reports" Metrologia 38, 301-309 (2011).
  // H2O2-water mixture density: W.C. Scumb et al., "Hydrogen Peroxide," Amer. Chem. Soc. Monograph (1955).
  // Input parameters:
  //   density = Liquid density [ug/cm3]
  //   T = Temperature of liquid ['C]
  // Output parameter:
  //   H2O2_d_to_massPercent = H2O2 mass percent [w-%]

  // Water density
  const wDensity = WaterDensity(t);

  // H2O2-water mixture density
  const B = Jb + Kb * t + Lb * t ** 2 + Mb * t ** 3;
  const c = Jc + Kc * t + Lc * t ** 2 + Mc * t ** 3;
  const d = Jd + Kd * t + Ld * t ** 2 + Md * t ** 3;

  let massPercent = 1;
  let massPercent2;
  let dd;
  let dd2;

  do {
    dd = density - (wDensity + B * massPercent + c * massPercent ** 2 + d * massPercent ** 3);
    massPercent2 = massPercent - 0.0000001;
    dd2 = density - (wDensity + B * massPercent2 + c * massPercent2 ** 2 + d * massPercent2 ** 3);

    massPercent = massPercent + dd / ((dd2 - dd) / 0.0000001);
  } while (Math.abs(dd) > 0.000001);

  return massPercent * 100;
};

const nebulizedLiquidFlow = (lFlow: number, lDensity: number, t: number, M: number) => {
  const massPercent = densityToLiquidMassConc(lDensity, t);
  let lFlowMass = lFlow * lDensity;

  if (M === MH2O) {
    lFlowMass = lFlowMass * (1 - massPercent / 100);
  } else if (M === MH2O2) {
    lFlowMass = lFlowMass * (massPercent / 100);
  }

  return lFlowMass / M;
};

const volumeFlowToMassFlow = (volFlow: number) => volFlow / (RLATM * 273.15);

const ppmToRs = (t: number, ch2o2: number, cWater: number, p: number) => {
  // Relative Saturation from ppm
  // This calculation is made in GIVEN PRESSURE = input variable!
  // Input parameters:
  //   t = Ambient temperature ['C]
  //   ch2o2 = H2O2 concentration [ppm]
  //   cWater = Water concentration [ppm]
  //   p = Ambient pressure [hPa]
  // Output:
  //   Relative saturation [%RS]

  const xhe = getXh(t, ch2o2, p);
  const xwe = 1 - xhe;
  const yw2 = wActivity(xwe, t);
  const pws = ews(t);
  const pwe = pws * xwe * yw2;
  const cpw = (cWater * p) / 1e6;
  return (cpw / pwe) * 100;
};

const getXh = (t: number, ch2o2: number, p: number) => {
  // Calculates H2O2 mole fraction from water-H2O2 vapor mixture by iteration
  // This calculation is made in GIVEN PRESSURE = input variable!

  const phs = ehs(t);
  const pws = ews(t);
  const phm = (ch2o2 * p) / 1e6;

  let xhe = phm / phs;
  let pwCalc;
  let pwe;
  let dpw;
  let xhe2;
  let dPw2;
  let deri;

  do {
    pwCalc = wActivity(1 - xhe, t) * pws * (1 - phm / (hActivity(1 - xhe, t) * phs));
    pwe = pws * (1 - xhe) * wActivity(1 - xhe, t);
    dpw = pwe - pwCalc;

    xhe2 = xhe + 0.000001;
    pwCalc = wActivity(1 - xhe2, t) * pws * (1 - phm / (hActivity(1 - xhe2, t) * phs));
    pwe = pws * (1 - xhe2) * wActivity(1 - xhe2, t);
    dPw2 = pwe - pwCalc;
    deri = (dPw2 - dpw) / 0.000001;
    xhe = xhe - dpw / deri;
  } while (Math.abs(dpw) > 0.00001);

  return xhe;
};

const mixtureTd = (vh2o2: number, pw: number, p: number, t: number) => {
  let td = t;
  // let xhe;
  // let xwe;
  // let yw;
  // let pws;
  // let pwe;
  let dd;
  // let td2;
  // let xhe2;
  // let xwe2;
  // let yw2;
  // let pws2;
  // let pwe2;
  // let dd2;

  do {
    //     Iterate mole fraction (xhe) for first guess/estimated Td
    // Calculate mixture saturation vapor pressure (pwe) for xhe
    const xhe = getXh(td, vh2o2, p);
    const xwe = 1 - xhe;
    const yw = wActivity(xwe, t);
    const pws = ews(td);
    const pwe = pws * xwe * yw;

    // Calculate difference of estimated Pwe to actual vapor pressure (Pw)
    dd = pw - pwe;

    // Make second guess for Td
    const td2 = td - 0.000001;

    // Iterate mole fraction (xhe) for second guess/estimated Td
    // Calculate mixture saturation vapor pressure (pwe) for Xhe
    const xhe2 = getXh(td2, vh2o2, p);
    const xwe2 = 1 - xhe2;
    const yw2 = wActivity(xwe2, t);
    const pws2 = ews(td2);
    const pwe2 = pws2 * xwe2 * yw2;

    // Calculate difference of second estimate Pwe to actual vapor pressure (Pw)
    const dd2 = pw - pwe2;

    // Calculate how close both estimations are and change next guess accordingly
    const derivv = (dd2 - dd) / 0.000001;
    td = td + dd / derivv;
  } while (Math.abs(dd) > 0.00001);

  return td;
};

// Simple half-life decompose model. 0-input will disable the calculation: half-life = inf
const decomposeH2O2 = (h2o2Ppm: number, halftime: number) => (halftime > 0 ? h2o2Ppm * (Math.LN2 / (halftime * 3600)) : 0);

export const freeEvaporation = (t: number, p: number, lh2o2: number) => {
  const waterMoles = (1 - lh2o2 / 100) * (1 / MH2O);
  const hpMoles = (lh2o2 / 100) * (1 / MH2O2);

  const xw = waterMoles / (waterMoles + hpMoles);
  const xh = hpMoles / (waterMoles + hpMoles);

  const yw = wActivity(xw, t);
  const yh = hActivity(xw, t);

  const pws = ews(t);
  const phs = ehs(t);

  const pw = xw * yw * pws;
  const ph = xh * yh * phs;

  // const ppmw = (pw * 10 ** 6) / p;
  const ppmh = (ph * 1e6) / p;

  const rh = (pw / pws) * 100;

  const rs = 100;

  return [rh, rs, ppmh];
};

export const openSystem = (lh2o2: number, t: number, p: number, gFlow: number, grh: number, lFlow: number) => {
  const pws = ews(t);
  const pw = rhToPw(grh, pws);

  const liquidDensity = liquidMassConcToDensity(lh2o2 / 100, t);

  const waterFromGas = flowToWetFlow(gFlow, p, pw);
  const GasMass = volumeFlowToMassFlow(gFlow);
  const waterFromliquid = nebulizedLiquidFlow(lFlow, liquidDensity, t, MH2O);
  const h2o2Fromliquid = nebulizedLiquidFlow(lFlow, liquidDensity, t, MH2O2);
  const totalMassFlow = waterFromGas + GasMass + waterFromliquid + h2o2Fromliquid;

  const cWater = ((waterFromGas + waterFromliquid) / totalMassFlow) * 1e6;

  let ch2o2 = (h2o2Fromliquid / totalMassFlow) * 1e6;
  let rh = (((cWater / 1e6) * p) / pws) * 100;
  let rs = ppmToRs(t, ch2o2, cWater, p);

  if (rs > 100 || rs < 0) {
    rs = 100;
    ch2o2 = NaN;
  }

  if (rh > 100) {
    rs = 100;
    rh = 100;
    ch2o2 = NaN;
  }

  return [rh, rs, ch2o2];
};

const createGraphData = (dataFunc: any, resolution: number) => {
  const duration = dataFunc.length;
  const timeStep = Math.floor(duration / resolution);

  const graphpoints = [];
  let i = 0;

  for (i; i <= duration - timeStep; i = i + timeStep) {
    graphpoints.push(dataFunc[i]);
  }
  // Last datapoint
  graphpoints.push(dataFunc[duration - 1]);

  const formattedData = {
    seconds: graphpoints.map((item: any) => {
      return item[0];
    }),
    relativeHumidity: graphpoints.map((item: any) => {
      return item[1];
    }),
    relativeSaturation: graphpoints.map((item: any) => {
      return item[2];
    }),
    h2o2Concentration: graphpoints.map((item: any) => {
      return item[3];
    }),
  };

  return formattedData;
};

export const dehumidify = (
  rh0: number,
  t0: number,
  p0: number,
  rh1: number,
  t1: number,
  p1: number,
  volume: number,
  duration: number,
  resolution: number
) => {
  const pws0 = ews(t0);
  const pws1 = ews(t1);

  const pw0 = rhToPw(rh0, pws0);
  const pw1 = rhToPw(rh1, pws1);

  const x0 = pwToX(pw0, p0, MAIR); // g/kg
  const x1 = pwToX(pw1, p1, MAIR); // g/kg

  const h0 = pwToH(pw0, t0, 0, p0, 28.96443, 1.006); // kJ/kg
  const h1 = pwToH(pw1, t1, 0, p1, 28.96443, 1.006); // kJ/kg

  const density = gasDensity(t1, p1, x1); // kg/m3

  const grams = (x0 - x1) * volume * density; // g
  const gramsPerHour = grams / (duration / 3600); // g/h

  const kW = (h1 - h0) * density * (volume / duration); // kJ/kg * kg/m3 * m3/s = kW

  let timeSeries = [];
  let i = 0;

  const xStep2 = (x1 - x0) / duration;
  const pwsStep2 = (pws1 - pws0) / duration;

  for (i; i <= duration; i += 1) {
    const rhInTime = pwToRh(xToPw(x0 + xStep2 * i, p1, MAIR), pws0 + pwsStep2 * i);
    timeSeries.push([i, rhInTime, rhInTime, 0]);
  }

  const graphdata = createGraphData(timeSeries, resolution);

  return [graphdata, gramsPerHour, kW];
};

export const injection = (
  volume: number,
  initRh: number,
  t: number,
  p: number,
  duration: number,
  injectionRate: number,
  initialH2O2Ppm: number,
  lh2o2: number,
  evEffic: number,
  decomp: number,
  resolution: number
) => {
  let timeSeries = [];
  let i = 0;

  const effectiveLh2o2 = lh2o2 * (evEffic / 100);

  const liquidDensity = liquidMassConcToDensity(effectiveLh2o2 / 100, t); // g/ml
  const injectionMassRate = injectionRate * liquidDensity; // g/min
  const initialWaterPpm = (((initRh / 100) * ews(t)) / p) * 1e6;

  let rh = initRh;
  let rs = ppmToRs(t, initialH2O2Ppm, initialWaterPpm, p);
  let h2o2Ppm = initialH2O2Ppm;
  let waterPpm = initialWaterPpm;
  let pw = (waterPpm / 1e6) * p;

  timeSeries.push([i, rh, rs, h2o2Ppm]);
  i++;

  const injectedMass = injectionMassRate / 60; // g
  const injectedWaterMass = injectedMass * (1 - effectiveLh2o2 / 100);
  const injectedH2O2Mass = injectedMass * (effectiveLh2o2 / 100);

  const waterMoles = injectedWaterMass / MH2O;
  const h2o2Moles = injectedH2O2Mass / MH2O2;

  const waterVapVolume = (waterMoles * RLATM * (t + 273.15)) / (p / 1013.25); // l
  const h2o2VapVolume = (h2o2Moles * RLATM * (t + 273.15)) / (p / 1013.25); // l

  const waterFromLiquidPpm = (waterVapVolume / (volume * 1000)) * 1e6; //ppm/s
  const h2o2FromLiquidPpm = (h2o2VapVolume / (volume * 1000)) * 1e6; //ppm/s

  for (i; i <= duration; i++) {
    // Decomposition
    const decompH2o2Ppm = decomposeH2O2(h2o2Ppm, decomp);

    h2o2Ppm = h2o2Ppm + h2o2FromLiquidPpm - decompH2o2Ppm;

    waterPpm = waterPpm + waterFromLiquidPpm + (decompH2o2Ppm * 2) / 3;

    pw = (waterPpm / 1e6) * p;

    rh = (pw / ews(t)) * 100;
    rs = ppmToRs(t, h2o2Ppm, waterPpm, p);

    if (rs >= 100 || rs < 0) {
      if (i !== 0) {
        rh = timeSeries[i - 1][1];
        rs = 100;
        h2o2Ppm = timeSeries[i - 1][3];
      } else {
        rh = initRh;
        rs = 100;
        h2o2Ppm = initialH2O2Ppm;
      }
    }

    timeSeries.push([i, rh, rs, h2o2Ppm]);
  }

  let td;
  if (rs >= 100) {
    td = t;
  } else {
    td = mixtureTd(h2o2Ppm, pw, p, t);
  }

  const graphdata = createGraphData(timeSeries, resolution);

  return [graphdata, rh, rs, h2o2Ppm, td];
};

export const aeration = (
  volume: number,
  initRh: number,
  t: number,
  p: number,
  duration: number,
  initialH2O2Ppm: number,
  decomp: number,
  ventilation: number,
  ventRh: number,
  ventT: number,
  catDecomp: number,
  resolution: number
) => {
  let timeSeries = [];
  let i = 0;

  const ventilationPw = rhToPw(ventRh, ews(ventT));
  const waterfromVentilationPpm = (ventilationPw / p) * 1e6;

  let h2o2Ppm = initialH2O2Ppm;
  let waterPpm = (((initRh / 100) * ews(t)) / p) * 1e6;
  let rh = initRh;
  let rs = ppmToRs(t, initialH2O2Ppm, waterPpm, p);
  let pw = (waterPpm / 1e6) * p;
  let totalDecomp = 0;

  // combine decompositions with inverse logic and avoid infinity
  if (decomp > 0 || catDecomp > 0) {
    const invDecomp = 1 / decomp;
    const invCatDecomp = 1 / catDecomp;
    totalDecomp = 1 / ((isFinite(invDecomp) ? invDecomp : 0) + (isFinite(invCatDecomp) ? invCatDecomp : 0));
  }

  const ventilationVolume = ventilation / 60; //m3/s

  const totalVolume = volume + ventilationVolume;

  for (i; i <= duration; i++) {
    timeSeries.push([i, rh, rs, h2o2Ppm]);
    const convertedPpm = decomposeH2O2(h2o2Ppm, totalDecomp);
    waterPpm = (volume / totalVolume) * waterPpm + (ventilationVolume / totalVolume) * waterfromVentilationPpm + (convertedPpm * 2) / 3;
    h2o2Ppm = (volume / totalVolume) * h2o2Ppm - convertedPpm;

    pw = (waterPpm / 1e6) * p;

    rh = (pw / ews(t)) * 100;
    rs = ppmToRs(t, h2o2Ppm, waterPpm, p);
  }

  let td;
  if (rs >= 100) {
    td = t;
  } else {
    td = mixtureTd(h2o2Ppm, pw, p, t);
  }

  const graphdata = createGraphData(timeSeries, resolution);

  return [graphdata, rh, rs, h2o2Ppm, td];
};
